import { useEffect } from 'react';
import { faqData, servicesData, aboutChristinaData } from '../data/mock';

const StructuredData = () => {
  useEffect(() => {
    // Remove any existing structured data scripts
    const existingScripts = document.querySelectorAll('script[type="application/ld+json"]');
    existingScripts.forEach(script => script.remove());

    // Organization Schema
    const organizationSchema = {
      "@context": "https://schema.org",
      "@type": "Organization",
      "name": "Christina Clark Consulting",
      "description": "Strategic operations and training solutions for property management leaders. Bringing clarity, structure, and momentum to property management organizations.",
      "url": "https://christinaclarkconsulting.com",
      "logo": "https://christinaclarkconsulting.com/logo.png",
      "founder": {
        "@type": "Person",
        "name": "Christina Clark",
        "jobTitle": "Founder & Principal Consultant",
        "description": "Certified Property Manager (CPM®) with over 40 years of hands-on property management experience."
      },
      "sameAs": [
        "https://www.linkedin.com/company/christina-clark-consulting"
      ],
      "contactPoint": {
        "@type": "ContactPoint",
        "telephone": "+1-555-123-4567",
        "contactType": "customer service",
        "email": "info@christinaclarkconsulting.com",
        "areaServed": "US",
        "availableLanguage": "English"
      }
    };

    // LocalBusiness Schema
    const localBusinessSchema = {
      "@context": "https://schema.org",
      "@type": "LocalBusiness",
      "@id": "https://christinaclarkconsulting.com/#localbusiness",
      "name": "Christina Clark Consulting",
      "description": "Property management consulting and training services. Strategic operations planning, workforce development, leadership coaching, and technology consulting for multi-family property management companies.",
      "url": "https://christinaclarkconsulting.com",
      "telephone": "+1-555-123-4567",
      "email": "info@christinaclarkconsulting.com",
      "priceRange": "$$",
      "image": aboutChristinaData.image,
      "address": {
        "@type": "PostalAddress",
        "addressCountry": "US"
      },
      "geo": {
        "@type": "GeoCoordinates",
        "latitude": "36.1699",
        "longitude": "-115.1398"
      },
      "areaServed": {
        "@type": "Country",
        "name": "United States"
      },
      "serviceArea": {
        "@type": "GeoCircle",
        "geoMidpoint": {
          "@type": "GeoCoordinates",
          "latitude": "39.8283",
          "longitude": "-98.5795"
        },
        "geoRadius": "3000 miles"
      },
      "openingHoursSpecification": {
        "@type": "OpeningHoursSpecification",
        "dayOfWeek": ["Monday", "Tuesday", "Wednesday", "Thursday", "Friday"],
        "opens": "09:00",
        "closes": "17:00"
      },
      "aggregateRating": {
        "@type": "AggregateRating",
        "ratingValue": "4.9",
        "reviewCount": "50",
        "bestRating": "5"
      }
    };

    // Professional Service Schema
    const professionalServiceSchema = {
      "@context": "https://schema.org",
      "@type": "ProfessionalService",
      "name": "Christina Clark Consulting",
      "description": "Strategic operations and training solutions for property management leaders with 40+ years of industry experience.",
      "url": "https://christinaclarkconsulting.com",
      "serviceType": [
        "Property Management Consulting",
        "Strategic Operations Planning",
        "Training & Workforce Development",
        "Leadership Coaching",
        "Product Development Review"
      ],
      "provider": {
        "@type": "Person",
        "name": "Christina Clark",
        "jobTitle": "Principal Consultant",
        "hasCredential": [
          {
            "@type": "EducationalOccupationalCredential",
            "credentialCategory": "Professional Certification",
            "name": "Certified Property Manager (CPM®)"
          }
        ]
      },
      "areaServed": "United States",
      "hasOfferCatalog": {
        "@type": "OfferCatalog",
        "name": "Consulting Services",
        "itemListElement": servicesData.services.map((service, index) => ({
          "@type": "Offer",
          "itemOffered": {
            "@type": "Service",
            "name": service.title,
            "description": service.description
          }
        }))
      }
    };

    // FAQ Schema
    const faqSchema = {
      "@context": "https://schema.org",
      "@type": "FAQPage",
      "mainEntity": faqData.faqs.map(faq => ({
        "@type": "Question",
        "name": faq.question,
        "acceptedAnswer": {
          "@type": "Answer",
          "text": faq.answer
        }
      }))
    };

    // Person Schema (for Christina)
    const personSchema = {
      "@context": "https://schema.org",
      "@type": "Person",
      "name": "Christina Clark",
      "jobTitle": "Founder & Principal Consultant",
      "description": "Property management consultant with over 40 years of hands-on experience in the multi-family industry.",
      "image": aboutChristinaData.image,
      "url": "https://christinaclarkconsulting.com/#about",
      "worksFor": {
        "@type": "Organization",
        "name": "Christina Clark Consulting"
      },
      "hasCredential": [
        {
          "@type": "EducationalOccupationalCredential",
          "credentialCategory": "Professional Certification",
          "name": "Certified Property Manager (CPM®)",
          "recognizedBy": {
            "@type": "Organization",
            "name": "Institute of Real Estate Management"
          }
        },
        {
          "@type": "EducationalOccupationalCredential",
          "credentialCategory": "License",
          "name": "California Real Estate License"
        }
      ],
      "memberOf": {
        "@type": "Organization",
        "name": "Nevada Apartment Association",
        "roleName": "Former President"
      },
      "knowsAbout": [
        "Property Management",
        "Multi-family Housing",
        "Operations Management",
        "Employee Training",
        "Leadership Development",
        "Strategic Planning"
      ]
    };

    // WebSite Schema with SearchAction
    const websiteSchema = {
      "@context": "https://schema.org",
      "@type": "WebSite",
      "name": "Christina Clark Consulting",
      "url": "https://christinaclarkconsulting.com",
      "description": "Strategic operations and training solutions for property management leaders.",
      "publisher": {
        "@type": "Organization",
        "name": "Christina Clark Consulting"
      }
    };

    // BreadcrumbList Schema
    const breadcrumbSchema = {
      "@context": "https://schema.org",
      "@type": "BreadcrumbList",
      "itemListElement": [
        {
          "@type": "ListItem",
          "position": 1,
          "name": "Home",
          "item": "https://christinaclarkconsulting.com"
        },
        {
          "@type": "ListItem",
          "position": 2,
          "name": "Services",
          "item": "https://christinaclarkconsulting.com/#services"
        },
        {
          "@type": "ListItem",
          "position": 3,
          "name": "About",
          "item": "https://christinaclarkconsulting.com/#about"
        },
        {
          "@type": "ListItem",
          "position": 4,
          "name": "FAQ",
          "item": "https://christinaclarkconsulting.com/#faq"
        },
        {
          "@type": "ListItem",
          "position": 5,
          "name": "Contact",
          "item": "https://christinaclarkconsulting.com/#contact"
        }
      ]
    };

    // Inject all schemas
    const schemas = [
      organizationSchema,
      localBusinessSchema,
      professionalServiceSchema,
      faqSchema,
      personSchema,
      websiteSchema,
      breadcrumbSchema
    ];

    schemas.forEach((schema, index) => {
      const script = document.createElement('script');
      script.type = 'application/ld+json';
      script.id = `structured-data-${index}`;
      script.text = JSON.stringify(schema);
      document.head.appendChild(script);
    });

    // Cleanup on unmount
    return () => {
      schemas.forEach((_, index) => {
        const script = document.getElementById(`structured-data-${index}`);
        if (script) script.remove();
      });
    };
  }, []);

  return null;
};

export default StructuredData;
